/**
 * Storezone — ErrorBoundary
 *
 * Catches JavaScript errors anywhere in child component trees,
 * logs errors, and displays a fallback UI instead of a white screen.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { ErrorState } from '@/components/ui/ErrorState'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[Storezone ErrorBoundary] Uncaught error:', error, errorInfo)
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null })
    window.location.href = '/'
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--sz-space-6, 24px)',
            background: 'var(--sz-bg, #f8fafc)',
          }}
        >
          <ErrorState
            title="Application Error"
            message={
              this.state.error?.message ||
              'An unexpected error occurred while loading the application.'
            }
            onRetry={this.handleReset}
          />
        </div>
      )
    }

    return this.props.children
  }
}
